// Build final CSS text for one compiled source and measure debug/map prep cost.
// Example: remap debug lines, compose CSS, then append source-map footer.
import type { LuisCompileDebugRule, LuisCompileResult } from '@lentystyle/compiler'
import type { LuisDebugFeatureApi, LuisMapFeatureApi } from '../../features'
import { getNow } from '../context-time'
import { composeMainCssInSourceOrder } from './css-compose'
import { composeInitialObservedCssInSourceOrder } from './css-compose-initial'
import { resolveActiveInitialObservedRules } from './initial-observed'

/** @internal Internal prepared-css payload used only inside the runtime build pipeline. */
export type PreparedRuntimeCss = {
  _remappedDebugRules: LuisCompileDebugRule[] | undefined
  _cssWithDebugMarkers: string
  _cssWithSourceMap: string
  _timing: {
    remapDurationMs?: number
    composeDurationMs?: number
    mapDurationMs?: number
  }
}

// Prepare final CSS text and timing details for runtime debug/map features.
/** @internal Prepare final CSS text and timing details for runtime debug/map features. */
export function prepareCompiledCss(
  compile: LuisCompileResult,
  entry: {
    styleTag: HTMLStyleElement
    rawSource: string
    debugSource: string
  },
  options: {
    compileSourceId: string
    mapEnabled: boolean
    lazyEnabled: boolean
    outputMode: 'full' | 'observed-only'
    compileDebugEnabled: boolean
    debugFeature?: LuisDebugFeatureApi
    mapFeature?: LuisMapFeatureApi
  },
): PreparedRuntimeCss {
  const remapStart = getNow()
  const remappedDebugRules = options.compileDebugEnabled
    ? (options.debugFeature?.remapDebugRulesToSource(compile.debugRules, entry.debugSource || entry.rawSource) ?? compile.debugRules)
    : undefined
  const remapDurationMs = options.compileDebugEnabled ? getNow() - remapStart : undefined

  const composeStart = getNow()
  const activeInitialObservedRules = options.lazyEnabled
    ? []
    : resolveActiveInitialObservedRules(compile.initialObservedRules ?? [])
  const cssWithDebugMarkers = options.outputMode === 'observed-only'
    ? composeInitialObservedCssInSourceOrder(
      compile,
      activeInitialObservedRules,
      remappedDebugRules,
      options.compileDebugEnabled,
      options.debugFeature,
    )
    : composeMainCssInSourceOrder(
      compile,
      activeInitialObservedRules,
      remappedDebugRules,
      options.compileDebugEnabled,
      options.debugFeature,
    )
  const composeDurationMs = getNow() - composeStart

  const mapStart = getNow()
  const cssWithSourceMap = options.mapEnabled && options.mapFeature
    ? options.mapFeature.buildCssWithLuisSourceMap(entry.styleTag, cssWithDebugMarkers, {
      enabled: true,
      sourceId: options.compileSourceId,
      sourceContent: entry.rawSource,
    })
    : cssWithDebugMarkers
  const mapDurationMs = options.mapEnabled && options.mapFeature ? getNow() - mapStart : undefined

  // Return final CSS payload and debug/map preparation timings.
  return {
    _remappedDebugRules: remappedDebugRules,
    _cssWithDebugMarkers: cssWithDebugMarkers,
    _cssWithSourceMap: cssWithSourceMap,
    _timing: {
      remapDurationMs,
      composeDurationMs,
      mapDurationMs,
    },
  }
}
